import type { PickedFile } from "@bundle/core";
import { useRef, useState, type DragEvent, type ReactNode } from "react";
import "./files.css";
import { Icon } from "./Icon.tsx";
import { droppedTree, pickedFiles, type PickedTreeFile } from "./media.tsx";

export type DropZoneProps = {
  /** Files dropped on the zone (folders and non-file drags are ignored). */
  onFiles: (files: PickedFile[]) => void;
  /** Overlay title while files are dragged over ("Drop files to share in #design"). */
  label: string;
  /** Smaller line under the title. */
  sublabel?: string;
  disabled?: boolean;
  /** Present = folders are accepted too: everything dropped, folders walked (used instead of `onFiles`). */
  onTree?: (files: PickedTreeFile[]) => void;
  testID: string;
  children?: ReactNode;
};

const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer.types).includes("Files");

/**
 * A pane-sized drop target: dragging files anywhere over it shows a full-pane overlay; dropping hands them to
 * `onFiles`. Fills its parent like a growing vertical Stack. Native: SwiftUI `.onDrop(of: [.fileURL, .image])`
 * (iPad/Mac), Compose `Modifier.dragAndDropTarget` (tablets, ChromeOS).
 */
export function DropZone({ onFiles, onTree, label, sublabel, disabled, testID, children }: DropZoneProps) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const enter = (e: DragEvent<HTMLDivElement>) => {
    if (disabled === true || !hasFiles(e)) return;
    e.preventDefault();
    depth.current += 1;
    setOver(true);
  };
  const leave = (e: DragEvent<HTMLDivElement>) => {
    if (disabled === true || !hasFiles(e)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  };
  return (
    <div
      className="b-dropzone"
      data-testid={testID}
      data-dragging={over ? "true" : undefined}
      onDragEnter={enter}
      onDragLeave={leave}
      onDragOver={(e) => {
        if (disabled === true || !hasFiles(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDrop={(e) => {
        if (disabled === true || !hasFiles(e)) return;
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        if (onTree !== undefined) {
          void droppedTree(e.dataTransfer).then((tree) => {
            if (tree.length > 0) onTree(tree);
          });
          return;
        }
        const files = pickedFiles(e.dataTransfer.files).filter((f) => f.size > 0 || f.mime !== "");
        if (files.length > 0) onFiles(files);
      }}
    >
      {children}
      {over ? (
        <div className="b-dropzone-overlay" role="status" aria-live="polite" data-testid={`${testID}-overlay`}>
          <span className="b-dropzone-icon">
            <Icon name="upload" size="icon.lg" color="accent.text" />
          </span>
          <p className="b-dropzone-title">{label}</p>
          {sublabel === undefined ? null : <p className="b-dropzone-subtitle">{sublabel}</p>}
        </div>
      ) : null}
    </div>
  );
}
